import type { AxiosRequestTransformer } from 'axios';
import { authHttpClient } from '@webmotors/consumer-shared-auth';
import { ENV } from '@/app/config';
import { setupAxiosObservability } from '@/services/observability';

// O @webmotors/consumer-shared-auth registra interceptors próprios no
// `authHttpClient` (request: anexa idToken / renova via refreshToken; response:
// 401 → redirect para login). Confiamos nessa implementação — apenas
// configuramos defaults, garantimos o prefixo `Bearer ` exigido pela API
// `ad/v1` e plugamos observabilidade, espelhando o padrão do consumer-chat.
authHttpClient.defaults.baseURL = ENV.API_BASE_URL;
authHttpClient.defaults.timeout = 30_000;

// A lib seta `Authorization = idToken` (sem prefixo). A API de "Meus Anúncios"
// exige `Authorization: Bearer <token>`. `transformRequest` roda DEPOIS dos
// interceptors de request — então pegamos o header já populado pela lib e
// adicionamos o prefixo antes do envio.
const ensureBearerPrefix: AxiosRequestTransformer = (data, headers) => {
	const auth = headers?.Authorization;
	if (typeof auth === 'string' && auth.length > 0 && !auth.startsWith('Bearer ')) {
		headers.Authorization = `Bearer ${auth}`;
	}
	return data;
};

const resolveTransformRequest = (
	transform: AxiosRequestTransformer | AxiosRequestTransformer[] | undefined
): AxiosRequestTransformer[] => {
	if (Array.isArray(transform)) return transform;
	if (transform) return [transform];
	return [];
};

const baseTransforms = resolveTransformRequest(authHttpClient.defaults.transformRequest);
authHttpClient.defaults.transformRequest = [...baseTransforms, ensureBearerPrefix];

// Transforms SEM o prefixo `Bearer`. O gateway `pf/v1/acesso/*` (ex.:
// `dados-usuario`) — diferente da API `ad/v1` — valida o `idToken` CRU, exatamente
// como a lib o seta e como a garagem/consumer-chat o enviam. Passar `Bearer <token>`
// nessas rotas resulta em 401. As chamadas a esse gateway devem usar este
// `transformRequest` por requisição para pular o `ensureBearerPrefix` (mantendo a
// serialização JSON do corpo).
const rawAuthTransformRequest = baseTransforms;

if (import.meta.env.DEV) {
	const decodeJwt = (token: string): Record<string, unknown> | null => {
		try {
			const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
			return JSON.parse(atob(raw.split('.')[1] ?? ''));
		} catch {
			return null;
		}
	};

	authHttpClient.interceptors.request.use((config) => {
		const auth = String(config.headers?.Authorization ?? '');
		const jwt = decodeJwt(auth);
		console.log(`[API →] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
			hasToken: auth.length > 0,
			tokenIssuer: jwt?.iss ?? 'NO TOKEN',
			tokenExp: jwt?.exp ? new Date((jwt.exp as number) * 1000).toISOString() : 'N/A'
		});
		return config;
	});

	authHttpClient.interceptors.response.use(
		(res) => {
			console.log(`[API ←] ${res.status} ${res.config.method?.toUpperCase()} ${res.config.url}`);
			return res;
		},
		(error) => {
			const s = error.response?.status;
			console.error(`[API ←] ${s} ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
				body: error.response?.data
			});
			if (s === 401) {
				const jwt = decodeJwt(String(error.config?.headers?.Authorization ?? ''));
				console.warn('[AUTH 401]', {
					issuer: jwt?.iss ?? 'NO TOKEN SENT',
					expired: jwt?.exp ? Date.now() > (jwt.exp as number) * 1000 : 'unknown'
				});
			}
			return Promise.reject(error instanceof Error ? error : new Error(String(error)));
		}
	);
}

setupAxiosObservability(authHttpClient);

const httpClient = authHttpClient;

export { httpClient, rawAuthTransformRequest };
